import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { InstructionsSet, InstructionsSetResponse } from '@common/interfaces';
import { UserService } from '@modules/auth/services';
import { environment } from 'environments/environment';
import { Observable, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

/**
 * Máximo de apariciones automáticas del modal de tutorial por módulo; al llegar aquí se
 * deja de mostrar aunque el usuario no haya marcado "No volver a mostrar". Regla única
 * para todos los consumidores (InstructionsModalService y tutoriales nativos).
 */
export const MAX_TUTORIAL_VIEWS = 3;

/** Prefijo de la cola de vistas pendientes de sincronizar; la clave se aísla por tenant y usuario. */
const PENDING_VIEWS_STORAGE_PREFIX = 'pendingInstructionsViews';

/**
 * Backoff exponencial del reintento automático de la cola pendiente tras un fallo
 * transitorio: 5s, 10s, 20s, 40s… con tope de 60s. Acota la ventana de sub-conteo en el
 * servidor a ≤60s desde que vuelve la conectividad, sin depender del próximo showModal.
 */
const FLUSH_RETRY_BASE_MS = 5000;
const FLUSH_RETRY_MAX_MS = 60000;

@Injectable({
    providedIn: 'root',
})
export class InstructionsService {
    API_URL = environment.API_URL;

    private instructionsCache = new Map<string, InstructionsSet>();
    private hasLoadedAll = false;
    private flushingPendingViews = false;
    private flushRetryAttempts = 0;
    private flushRetryTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(private http: HttpClient, private userService: UserService) {}

    getInstructionsSetByName(instructionsSetName: string): Observable<InstructionsSetResponse> {
        if (this.instructionsCache.has(instructionsSetName)) {
            const cached = this.instructionsCache.get(instructionsSetName)!;
            return of({
                title: 'Instructions',
                message: 'Loaded from cache',
                data: cached,
            });
        }

        if (!this.hasLoadedAll) {
            return this.loadAllInstructions().pipe(
                map(() => {
                    const instruction = this.instructionsCache.get(instructionsSetName);
                    if (instruction) {
                        return {
                            title: 'Instructions',
                            message: 'Loaded with bulk load',
                            data: instruction,
                        };
                    }
                    throw new Error(`Instruction set '${instructionsSetName}' not found`);
                }),
            );
        }

        return this.http.get<InstructionsSetResponse>(`${this.API_URL}/instructions/${instructionsSetName}`);
    }

    private loadAllInstructions(): Observable<void> {
        return this.http.get<{ title: string; message: string; data: { [key: string]: InstructionsSet } }>(`${this.API_URL}/instructions/all`).pipe(
            tap(response => {
                Object.values(response.data).forEach(instruction => {
                    this.instructionsCache.set(instruction.name, instruction);
                });
                this.hasLoadedAll = true;
            }),
            map(() => void 0),
        );
    }

    doNotShowAgain(instructionsSetName: string) {
        return this.http.get<any>(`${this.API_URL}/instructions/do-not-show-again/${instructionsSetName}`);
    }

    registerView(instructionsSetName: string) {
        return this.http.post<any>(`${this.API_URL}/instructions/register-view/${instructionsSetName}`, {});
    }

    /**
     * Registro de vista con respaldo local (FINA-2939 / I-05, fail-open): el contador de
     * user$.instructionsViews avanza de inmediato (optimistic UI) y el incremento se encola
     * en localStorage; la cola se drena contra el servidor aquí mismo y en cada showModal.
     * El servidor sigue siendo la única fuente de verdad (AC-6): el respaldo local solo
     * puentea la ventana de fallo en este dispositivo.
     */
    registerViewTracked(instructionsSetName: string): void {
        if (!this.userService.user$.getValue()) return;
        this.incrementLocalViews(instructionsSetName);
        this.addPendingView(instructionsSetName);
        this.flushPendingViews();
    }

    /**
     * Reintenta las vistas encoladas que no llegaron al servidor. Un incremento por POST,
     * en serie. Ante un fallo transitorio, la cola se detiene y se auto-reprograma con
     * backoff exponencial (ver FLUSH_RETRY_BASE_MS), además del drenado de cada showModal.
     */
    flushPendingViews(): void {
        if (this.flushingPendingViews) return;
        const pending = this.getPendingViews();
        if (!Object.keys(pending).some(name => pending[name] > 0)) return;
        this.flushingPendingViews = true;
        this.sendNextPendingView();
    }

    private sendNextPendingView(): void {
        const pending = this.getPendingViews();
        const name = Object.keys(pending).find(key => pending[key] > 0);
        if (!name) {
            this.flushingPendingViews = false;
            this.flushRetryAttempts = 0; // cola drenada: reinicia el backoff
            return;
        }

        this.registerView(name)
            .pipe(take(1))
            .subscribe({
                next: response => {
                    this.flushRetryAttempts = 0; // el servidor respondió: reinicia el backoff
                    this.decrementPendingView(name);
                    this.reconcileLocalViews(name, response?.data?.views);
                    this.sendNextPendingView();
                },
                error: (err: any) => {
                    // 400/404: el set nunca va a ser aceptado (nombre inválido/inexistente);
                    // descartar para no reintentar indefinidamente. El resto queda encolado.
                    if (err?.status === 400 || err?.status === 404) {
                        this.decrementPendingView(name);
                        this.sendNextPendingView();
                        return;
                    }
                    console.error(err);
                    this.flushingPendingViews = false;
                    this.scheduleFlushRetry();
                },
            });
    }

    /**
     * Programa un único reintento diferido de la cola (sin acumular timers). El backoff
     * crece por intento fallido y se reinicia en cuanto el servidor vuelve a responder.
     * Si al disparar no hay usuario en sesión o la cola está vacía, flushPendingViews no hace nada.
     */
    private scheduleFlushRetry(): void {
        if (this.flushRetryTimer) return;
        const delay = Math.min(FLUSH_RETRY_BASE_MS * 2 ** this.flushRetryAttempts, FLUSH_RETRY_MAX_MS);
        this.flushRetryAttempts++;
        this.flushRetryTimer = setTimeout(() => {
            this.flushRetryTimer = null;
            this.flushPendingViews();
        }, delay);
    }

    private incrementLocalViews(instructionsSetName: string): void {
        const user = this.userService.user$.getValue();
        if (!user) return;
        const views = (user.instructionsViews?.[instructionsSetName] ?? 0) + 1;
        user.instructionsViews = { ...(user.instructionsViews ?? {}), [instructionsSetName]: views };
        this.userService.user = user;
        this.persistViewsToLocalStorage(user.instructionsViews);
    }

    /** El conteo local puede ir por delante del servidor mientras haya pendientes: se queda el mayor. */
    private reconcileLocalViews(instructionsSetName: string, serverViews: number): void {
        const user = this.userService.user$.getValue();
        if (!user || typeof serverViews !== 'number') return;
        const views = Math.max(user.instructionsViews?.[instructionsSetName] ?? 0, serverViews);
        user.instructionsViews = { ...(user.instructionsViews ?? {}), [instructionsSetName]: views };
        this.userService.user = user;
        this.persistViewsToLocalStorage(user.instructionsViews);
    }

    /** Mismo patrón que updateSalePreferences: persistir en userData para sobrevivir el reload. */
    private persistViewsToLocalStorage(instructionsViews: { [name: string]: number }): void {
        try {
            const userData = JSON.parse(localStorage.getItem('userData') || 'null');
            if (!userData) return;
            userData.instructionsViews = instructionsViews;
            localStorage.setItem('userData', JSON.stringify(userData));
        } catch (err) {
            // userData corrupto o cuota de storage excedida: no romper el flush; user$ en
            // memoria sigue al día y el conteo real vive en el servidor.
            console.error(err);
        }
    }

    private getPendingStorageKey(): string | null {
        const user = this.userService.user$.getValue();
        if (!user?._id || !user?.tenantId) return null;
        return `${PENDING_VIEWS_STORAGE_PREFIX}:${user.tenantId}:${user._id}`;
    }

    private getPendingViews(): { [name: string]: number } {
        const key = this.getPendingStorageKey();
        if (!key) return {};
        try {
            const parsed = JSON.parse(localStorage.getItem(key) || '{}');
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch {
            // Storage corrupto: descartar la cola para no bloquear el flush (self-healing).
            localStorage.removeItem(key);
            return {};
        }
    }

    private addPendingView(instructionsSetName: string): void {
        const pending = this.getPendingViews();
        pending[instructionsSetName] = (pending[instructionsSetName] ?? 0) + 1;
        this.setPendingViews(pending);
    }

    private decrementPendingView(instructionsSetName: string): void {
        const pending = this.getPendingViews();
        pending[instructionsSetName] = (pending[instructionsSetName] ?? 1) - 1;
        if (pending[instructionsSetName] <= 0) delete pending[instructionsSetName];
        this.setPendingViews(pending);
    }

    private setPendingViews(pending: { [name: string]: number }): void {
        const key = this.getPendingStorageKey();
        if (!key) return;
        try {
            if (Object.keys(pending).length === 0) localStorage.removeItem(key);
            else localStorage.setItem(key, JSON.stringify(pending));
        } catch (err) {
            // Cuota de storage excedida: la cola local se pierde para este ciclo, pero el
            // flush sigue vivo y el contador optimista en user$ ya avanzó.
            console.error(err);
        }
    }
}
